/**
 * /terms and /privacy, on the panel the game already has.
 *
 * It used to carry its own cut — art/legal-cut, 4.5 MB of background, panel and a tabs band drawn for these two
 * documents alone. The owner, 21.9.2026: build it from the ready-made components. So it is the notifications frame,
 * the same one Blocked now uses: a title with its rule, the × drawn into the corner, a body, and one pill at the foot.
 * The two tabs are gone with the art they needed — the title says which document you are reading and the foot pill is
 * the way to the other one, which is also the only thing the tabs ever did.
 *
 * The document scrolls inside the reading surface — the one place a panel scrolls: it is a document, not a game state.
 * Its bar is the avatar picker's rail, the owner's own drawing, dragged the same way.
 */
import { art } from '../art';
import { ScrollRail } from '../ui/ScrollRail';
import { useEffect, useRef } from 'preact/hooks';
import E from '../layouts/notif-cut-2026-09-15/pieces.json';
import LIVE from '../layouts/notif-cut-2026-09-15/live.json';
import { route } from '../state/store';
import { TERMS, TERMS_UPDATED } from '../legal/terms.en';
import { PRIVACY, PRIVACY_UPDATED } from '../legal/privacy.en';
import { WITHDRAWAL, WITHDRAWAL_UPDATED } from '../legal/withdrawal.en';
import { Ink, type InkBox } from '../ink';
import { sfx } from '../audio/sfx';

type B = { x: number; y: number; w: number; h: number };
type Cut = { id: string; x: number; y: number; w: number; h: number };
const find = (list: Cut[], id: string): B => { const c = list.find((p) => p.id === id); if (!c) throw new Error(`legal piece missing: ${id}`); return c; };
type LiveBox = { x: number; y: number; w: number; h: number; lane: B };
const L = LIVE as Record<string, LiveBox>;
const lv = (id: string, text: string, w?: number): InkBox => { const b = L[id]; if (!b) throw new Error(`legal live text missing: ${id}`); const W = w ?? b.w; const cx = b.x + b.w / 2; return { x: w ? cx - W / 2 : b.x, y: b.y, w: W, h: b.h, text, rw: b.w }; };
const hs = (b: B) => ({ left: b.x, top: b.y, width: b.w, height: b.h });
/** the reading surface inside the panel's body, and the rail beside it */
const READ = { x: 130, y: 648, w: 664, h: 548 };
const RAIL = { x: READ.x + READ.w + 6, y: READ.y, w: 24, h: READ.h };
const CLOSE = { x: 714, y: 522, w: 116, h: 116 };                 // the × drawn into the panel's corner
/** the picker's rail art: one thumb capsule on a track, the capsule rows 0-129 (measured) */

type Doc = 'terms' | 'privacy' | 'withdrawal';
const MD_LINK = /\[([^\]]+)\]\((TERMS|PRIVACY|WITHDRAWAL)_v[\d_]+\.en\.md\)/;
const DOCS: Record<Doc, { body: { h: string; p: string[] }[]; updated: string; title: string; word: string; pill: string }> = {
  terms: { body: TERMS, updated: TERMS_UPDATED, title: 'Terms of Service', word: 'TERMS', pill: 'TERMS OF SERVICE' },
  privacy: { body: PRIVACY, updated: PRIVACY_UPDATED, title: 'Privacy Notice', word: 'PRIVACY', pill: 'PRIVACY NOTICE' },
  withdrawal: { body: WITHDRAWAL, updated: WITHDRAWAL_UPDATED, title: 'Withdrawal instructions', word: 'WITHDRAWAL', pill: 'WITHDRAWAL' },
};
export function Legal({ doc }: { doc: Doc }) {
  const d = DOCS[doc]; const body = d.body; const updated = d.updated;
  // the foot pill: terms ↔ privacy as before; the withdrawal instructions lead back to the terms and are linked from both documents
  const other: Doc = doc === 'terms' ? 'privacy' : 'terms';
  const back = () => { sfx.tap(); history.pushState(null, '', '/'); route.value = { name: 'home' }; };
  const open = (d: Doc) => { if (d === doc) return; sfx.tap(); history.pushState(null, '', `/${d}`); route.value = { name: 'legal', doc: d }; };
  // the approved texts link each other in markdown ([Privacy Notice](PRIVACY_v2_2.en.md)): a link to that document here
  // (19.10.2026: Terms showed the raw brackets); split() hands back text, label, document, text, …
  const para = (t: string) => t.split(MD_LINK).map((x, i, all) => i % 3 === 0 ? x : i % 3 === 2 ? null : (() => {
    const to = all[i + 1]!.toLowerCase() as Doc;
    return <a key={i} href={`/${to}`} onClick={(e) => { e.preventDefault(); open(to); }}>{x}</a>;
  })());
  const lane = useRef<HTMLDivElement>(null);
  useEffect(() => { lane.current?.scrollTo({ top: 0 }); }, [doc]);

  const panel = find(E as Cut[], 'panel');
  return (
    <div class="screen abs enter legal60" data-testid={`legal-${doc}`}>
      <img class="cut-bg" src={art('/art/login-cut/background.png')} alt="" draggable={false} />
      <div class="sheet cut43" role="dialog" aria-label={d.title}>
        <img class="cut" src={art('/art/notif-cut/empty/panel.png')} alt="" draggable={false} style={{ ...hs(panel), zIndex: 20 }} />
        <Ink box={lv('title', d.word, 440)} text={d.word} class="st-t-title" align="center" maxW={430} z={46} />
        <button class="cut-hit" type="button" data-testid="legal-back" aria-label="Close" style={hs(CLOSE)} onClick={back} />
        {/* the document, scrolling inside the reading surface */}
        <div class="lg-read" ref={lane} style={{ ...hs(READ), zIndex: 30 }} data-testid="legal-body">
          <h2>{d.title}</h2>
          <p class="lg-meta">Updated {updated}</p>
          {body.map((s) => <section key={s.h}><h3>{s.h}</h3>{s.p.map((t, i) => <p key={i}>{para(t)}</p>)}</section>)}
          {doc !== 'withdrawal' && <p><a href="/withdrawal" data-testid="legal-withdrawal" onClick={(e) => { e.preventDefault(); open('withdrawal'); }}>Withdrawal instructions and model withdrawal form</a></p>}
        </div>
        {/* the game's one scrollbar, the same two drawn pieces every list wears */}
        <div class="lg-rail" style={{ ...hs(RAIL), position: 'absolute', zIndex: 31 }}>
          <ScrollRail target={lane} w={RAIL.w} testid="legal-scrollbar" />
        </div>
        {/* the panel's own pill at the foot: the way to the other document, which is all the two tabs ever did */}
        <Ink box={{ ...lv('cta', 'PRIVACY NOTICE', 500), x: 220 }} text={DOCS[other].pill} class="nf-t-cta" align="center" maxW={500} z={46} />
        <button class="cut-hit" type="button" data-testid={`legal-tab-${other}`} aria-label={DOCS[other].title} style={hs({ x: 170, y: 1210, w: 600, h: 116 })} onClick={() => open(other)} />
      </div>
    </div>
  );
}
